/**
 * PostHog bootstrap — the ONLY place posthog.init is called.
 *
 * Config comes from window.env (injected per environment by deployment configs):
 *   POSTHOG_KEY  — project API key; NO KEY = analytics fully OFF (local dev, envs
 *                  that haven't opted in). This replaces the old hostname gating.
 *   POSTHOG_HOST — optional; defaults to the EU cloud ingestion host.
 *
 * Identification is driven by a Zustand appStore subscription (no wiring needed
 * in auth flows): whenever the affiliate lands in the store, the user is
 * identified; `is_test` users are opted out entirely.
 */
import posthog from 'posthog-js';

import { useAppStore } from '@/modules/shared/store/appStore';

import { identifyAnalyticsUser } from './identify';

let initialized = false;

export function isAnalyticsEnabled(): boolean {
  return initialized;
}

export function initPostHog(): void {
  const key = window.env?.POSTHOG_KEY;
  if (!key || initialized) return;

  posthog.init(key, {
    api_host: window.env?.POSTHOG_HOST || 'https://eu.i.posthog.com',
    defaults: '2026-05-30',
    autocapture: true,
    // Page-view and rage-click tracking are off deliberately. Both are on by
    // default under `defaults: '2026-05-30'` (capture_pageview defaults to
    // 'history_change', rageclick to a config object), so they have to be
    // switched off explicitly — removing these lines re-enables them.
    capture_pageview: false,
    rageclick: false,
    // $pageleave only means anything paired with a $pageview. The explicit
    // `true` here used to force it on for every page; with pageviews off it
    // would emit orphan leave events, so it goes off with them.
    capture_pageleave: false,
    // Surveys are not used on an internal tool, and they cost a separate 102 KB script on
    // every load, 83% of it unused (Lighthouse on /create-leads and /ops-dashboard,
    // 2026-09-19). On by default under `defaults: '2026-05-30'`, so switching it off has to
    // be explicit — removing this line re-enables the download.
    disable_surveys: true,
    person_profiles: 'identified_only',
  });
  initialized = true;

  // Identify on every flow that lands an affiliate in the store (login,
  // affiliate selection, page-load hydration) — no per-flow wiring.
  let lastAffiliate: unknown = null;
  useAppStore.subscribe((state) => {
    if (state.affiliate && state.affiliate !== lastAffiliate) {
      lastAffiliate = state.affiliate;
      identifyAnalyticsUser(state.affiliate, state.user);
    }
  });

  const existingAffiliate = useAppStore.getState().affiliate;
  if (existingAffiliate) {
    lastAffiliate = existingAffiliate;
    identifyAnalyticsUser(existingAffiliate, useAppStore.getState().user);
  }
}

/** Call on logout — clears the persisted device/person identity. */
export function resetAnalytics(): void {
  if (!initialized) return;
  posthog.reset();
}
